import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  transactionCategories, 
  transactionTypes, 
  paymentMethods,
  currencies 
} from '@/lib/foco-calculations';
import { CreateTransactionInput, TransactionType, PaymentMethod, Currency } from '@/hooks/useTransactions';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useProfile } from '@/hooks/useProfile';
import { useDebts } from '@/hooks/useDebts';
import { Badge } from '@/components/ui/badge';
import { useBudgetEmailAlert } from '@/hooks/useBudgetEmailAlert';

const formSchema = z.object({
  date: z.string().min(1, 'La fecha es requerida'),
  type: z.enum(['income', 'fixed_expense', 'variable_expense', 'saving', 'investment', 'debt_payment']),
  amount: z.coerce.number().positive('El monto debe ser mayor a 0'),
  category: z.string().min(1, 'La categoría es requerida'),
  payment_method: z.enum(['cash', 'debit_card', 'credit_card', 'transfer', 'check', 'other']),
  currency: z.enum(['USD', 'CRC']),
  notes: z.string().optional(),
  debt_id: z.string().optional().nullable(),
});

interface TransactionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateTransactionInput) => void;
  initialData?: Partial<CreateTransactionInput>;
  isEditing?: boolean;
}

export function TransactionForm({ 
  open, 
  onOpenChange, 
  onSubmit, 
  initialData,
  isEditing = false 
}: TransactionFormProps) {
  const { profile } = useProfile();
  const { activeDebts } = useDebts();
  const { checkAndSendBudgetAlert } = useBudgetEmailAlert();
  const defaultCurrency = profile?.default_currency || 'CRC';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: initialData?.date || new Date().toISOString().split('T')[0],
      type: initialData?.type || 'income',
      amount: initialData?.amount || 0,
      category: initialData?.category || '',
      payment_method: initialData?.payment_method || 'cash',
      currency: initialData?.currency || defaultCurrency,
      notes: initialData?.notes || '',
      debt_id: initialData?.debt_id || null,
    },
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      // Use setTimeout to avoid DOM conflicts with Select components
      const timer = setTimeout(() => {
        form.reset({
          date: initialData?.date || new Date().toISOString().split('T')[0],
          type: initialData?.type || 'income',
          amount: initialData?.amount || 0,
          category: initialData?.category || '',
          payment_method: initialData?.payment_method || 'cash',
          currency: initialData?.currency || defaultCurrency,
          notes: initialData?.notes || '',
          debt_id: initialData?.debt_id || null,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, initialData]);

  // Update currency when profile loads and it's a new transaction
  useEffect(() => {
    if (!isEditing && !initialData?.currency && profile?.default_currency) {
      form.setValue('currency', profile.default_currency);
    }
  }, [profile?.default_currency, isEditing, initialData?.currency, form]);

  const selectedType = form.watch('type') as keyof typeof transactionCategories;
  const selectedCurrency = form.watch('currency');
  const selectedDebtId = form.watch('debt_id');
  const categories = transactionCategories[selectedType] || [];
  const currencySymbol = currencies.find(c => c.value === selectedCurrency)?.symbol || '₡';
  const isDebtPayment = selectedType === 'debt_payment';

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    // Check if budget is exceeded and send email alert for variable/fixed expenses
    if (values.type === 'variable_expense' || values.type === 'fixed_expense') {
      await checkAndSendBudgetAlert(values.category, values.amount, values.currency);
    }

    const submitData = {
      ...values,
      debt_id: values.type === 'debt_payment' ? values.debt_id : null,
    };
    onSubmit(submitData as CreateTransactionInput);
    form.reset();
    onOpenChange(false);
  };

  // Format currency for display
  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar transacción' : 'Nueva transacción'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.setValue('category', '');
                        if (value !== 'debt_payment') {
                          form.setValue('debt_id', null);
                        }
                      }} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona el tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {transactionTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Debt selector - only show for debt_payment type */}
              {isDebtPayment && activeDebts.length > 0 && (
                <FormField
                  control={form.control}
                  name="debt_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vincular a deuda (Bola de Nieve)</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona la deuda a pagar" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {activeDebts.map((debt) => (
                            <SelectItem key={debt.id} value={debt.id}>
                              <div className="flex items-center justify-between gap-2">
                                <span>{debt.name}</span>
                                <Badge variant="secondary" className="ml-2">
                                  {formatCurrency(debt.current_balance, selectedCurrency)}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Al vincular, el saldo de la deuda se actualizará automáticamente
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isDebtPayment && activeDebts.length === 0 && (
                <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                  💡 Tip: Registra tus deudas en la sección "Bola de Nieve" para vincular los pagos y rastrear tu progreso automáticamente.
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Moneda</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Moneda" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currencies.map((currency) => (
                            <SelectItem key={currency.value} value={currency.value}>
                              {currency.symbol} {currency.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Monto ({currencySymbol})</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0.00" 
                          step="0.01"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoría</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona la categoría" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-60">
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="payment_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Método de pago</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona el método" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paymentMethods.map((method) => (
                          <SelectItem key={method.value} value={method.value}>
                            {method.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notas (opcional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Añade una descripción..." 
                        className="resize-none"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {isEditing ? 'Guardar cambios' : 'Crear transacción'}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}